import { Fragment } from 'react';

import CloseIcon from '@mui/icons-material/Close';

import { Box, ButtonBase, Typography } from '@/shared/ui';

import { SHOW_SELECTED_URL_PARAM } from '@/config/constants';
import { transformCamelCaseToSpaces } from '@/shared/lib/format/transformCamelCaseToSpaces';
import { FILTER_KEYS, NEW_DATA_URL_PARAM } from '../../../constants';
import { useFilter } from '../../../state/useFilter';
import ShowSelectedSwitch from '../ShowSelectedSwitch';

const FILTER_GROUP_KEYS = Object.values(FILTER_KEYS);

const wrapperSx = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: '8px',
  py: 2,
  borderBottom: '1px solid',
  borderColor: 'gx.actionBarButtonTile',
  backgroundColor: 'gx.mainBackground'
};

const groupLabelSx = {
  color: 'gx.text',
  fontSize: 14,
  fontWeight: 500
};

// Extra margin on top of the base 8px flex gap, so a new group's name sits
// 16px away from whatever precedes it (the New Data chip or the prior group's
// last chip), while everything else keeps the 8px gap.
const groupSeparatorSx = { ml: 1 };

const chipSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  height: 34,
  px: 1,
  backgroundColor: 'gx.actionBarButtonTile',
  color: 'gx.text',
  fontSize: 14,
  '&:hover': { color: 'gx.highlight' }
};

const closeIconSx = { fontSize: 12 };

// ml on top of the wrapper's base 8px gap gives a 24px separation from the
// last tag, so the trailing controls still read as part of the same row
// instead of being pushed away from the tags.
const trailingControlsSx = {
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  ml: 2
};

const clearAllButtonSx = { color: 'gx.text', textDecoration: 'underline', px: 0.2, fontSize: 14 };
const hiddenClearAllButtonSx = { ...clearAllButtonSx, visibility: 'hidden' } as const;

/** Active facet-filter values grouped by column, the standalone New Data toggle, and the Clear all / Show selected only controls. Renders nothing while no filter (including Show selected only) is applied. */
const ActiveFilterChips = () => {
  const { filters, toggleFilterOption, setFilter, clearAllFilters, isAnyFilterApplied } =
    useFilter();

  const groups = FILTER_GROUP_KEYS.map(key => ({
    key,
    label: transformCamelCaseToSpaces(key),
    values: filters[key]
  })).filter(group => group.values.length > 0);

  const isNewDataApplied = filters[NEW_DATA_URL_PARAM];
  // isAnyFilterApplied excludes the show-selected toggle itself, so also check
  // it directly — otherwise turning it on with no other filter applied would
  // hide the only control that can turn it back off.
  const hasActiveState = isAnyFilterApplied || filters[SHOW_SELECTED_URL_PARAM];

  if (!hasActiveState) return null;

  return (
    <Box sx={wrapperSx}>
      {isNewDataApplied && (
        <ButtonBase
          aria-label='Remove New Data filter'
          sx={chipSx}
          onClick={() => {
            setFilter(NEW_DATA_URL_PARAM, false);
          }}
        >
          <Typography size={14}>New Data</Typography>
          <CloseIcon sx={closeIconSx} />
        </ButtonBase>
      )}
      {groups.map(({ key, label, values }, index) => {
        const isFirstItem = index === 0 && !isNewDataApplied;
        return (
          <Fragment key={key}>
            <Typography sx={isFirstItem ? groupLabelSx : [groupLabelSx, groupSeparatorSx]}>
              {label}:
            </Typography>
            {values.map(value => (
              <ButtonBase
                key={value}
                aria-label={`Remove ${value} filter`}
                sx={chipSx}
                onClick={() => {
                  toggleFilterOption(key, value);
                }}
              >
                <Typography size={14}>{value}</Typography>
                <CloseIcon sx={closeIconSx} />
              </ButtonBase>
            ))}
          </Fragment>
        );
      })}
      <Box sx={trailingControlsSx}>
        <ButtonBase
          sx={isAnyFilterApplied ? clearAllButtonSx : hiddenClearAllButtonSx}
          tabIndex={isAnyFilterApplied ? 0 : -1}
          onClick={clearAllFilters}
        >
          Clear all
        </ButtonBase>
        <ShowSelectedSwitch />
      </Box>
    </Box>
  );
};

export default ActiveFilterChips;
